import { Sparkles } from 'lucide-react';

const Overview = () => {
  return (
    <section className="container mx-auto px-4 py-12 relative z-10">
      <div className="max-w-7xl mx-auto w-full">
        {/* Top taglines */}
        <div className="text-center space-y-4 mb-12">
          <p className="text-xl text-slate-600 font-medium flex items-center justify-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500 animate-pulse" />
            Simplify finances, streamline tasks, and grow smarter.
          </p>
          <p className="text-lg text-slate-500">
            No dashboards. No learning curves. Just Carto.
          </p>
        </div>

        {/* Main layout with phone and tools */}
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto mb-8">
          {/* Left side - SMS Interface */}
          <div className="flex justify-center">
            {/* iPhone-style messaging interface */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-[3rem] p-4 max-w-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105">
              {/* iPhone header */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-[2.5rem] px-6 py-4 text-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">9:41</div>
                  <div className="w-20 h-6 bg-black rounded-full relative">
                    <div className="absolute inset-1 bg-gray-800 rounded-full"></div>
                  </div>
                  <div className="flex gap-1 text-sm">
                    <span className="animate-pulse">📶</span>
                    <span>🔋</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">C</span>
                  </div>
                  <div>
                    <div className="font-semibold">Carto</div>
                    <div className="text-xs text-green-400 flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      AI
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Messages */}
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-b-[2.5rem] px-4 py-6 space-y-4 min-h-[400px]">
                <div className="flex justify-start animate-fade-in">
                  <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white rounded-2xl rounded-bl-md px-4 py-3 max-w-xs text-left shadow-lg text-sm">
                    Revenue is up 15% vs last month! 📈 Your biggest win was the Johnson project. Cash flow looks healthy, but I noticed 3 overdue invoices worth $2,400. Want me to send follow-ups?
                  </div>
                </div>
                
                <div className="flex justify-end animate-fade-in" style={{animationDelay: '0.5s'}}>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 max-w-xs text-left shadow-lg">
                    Yes, send those follow-ups.
                  </div>
                </div>
                
                <div className="flex justify-start animate-fade-in" style={{animationDelay: '1s'}}>
                  <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white rounded-2xl rounded-bl-md px-4 py-3 max-w-xs text-left shadow-lg">
                    Done! ✅ Sent personalized follow-ups to all 3 clients. I'll track responses and update you.
                  </div>
                </div>
                
                <div className="flex justify-start animate-fade-in" style={{animationDelay: '1.5s'}}>
                  <div className="bg-gradient-to-r from-gray-600 to-gray-500 text-gray-300 rounded-2xl rounded-bl-md px-4 py-2 text-xs shadow-lg">
                    Your AI COO for Small Business
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Integrations Container */}
          <div className="space-y-8">
            {/* Integrations Container */}
            <div className="bg-gradient-to-br from-white via-blue-50/50 to-indigo-50/30 rounded-2xl p-8 border border-blue-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  Built-in Integrations
                </h2>
                <p className="text-slate-600 text-sm">
                  All from a single chat
                </p>
              </div>

              {/* Tools you already use */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 text-center">The tools you already use</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">QuickBooks</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Gmail</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Square</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Yelp</span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center justify-center mb-8">
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
              </div>

              {/* AI tools section */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4 text-center">The AI you don't have time to learn</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Regie.ai</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Crew.ai</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">Levity.ai</span>
                  </div>
                  <div className="bg-white border border-blue-100 rounded-lg px-4 py-4 text-center shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <span className="text-slate-700 font-medium text-sm">AdCreative.ai</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="text-center">
          <p className="text-slate-600 text-lg">
            Text your way through QuickBooks, Square, and the latest in AI — all from one chat.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Overview;
